// File: frontend/src/components/Dashboard/GlobalStatsCard.js

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { FiChevronLeft, FiChevronRight, FiInfo } from 'react-icons/fi';
import MapComponent from './MapComponent';
import mapData from '../../data/mapbox_data_for_dashboard.json';
import styles from './GlobalStatsCard.module.css';

const GlobalStatsCard = () => {
  const { t } = useLanguage();
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
  const [styleLoaded, setStyleLoaded] = useState(false);
  const [activeMapCategory, setActiveMapCategory] = useState('allegory_intelligence');
  const [mapKey, setMapKey] = useState('initial');

  const handleStyleLoad = useCallback((loaded) => {
    setStyleLoaded(loaded);
  }, []);

  // Define the categories and their stats
  const categories = useMemo(() => [
    {
      id: 'insurance',
      title: t('stats.categories.insurance'),
      stats: [
        { label: t('stats.insurance.underwriting_capacity'), value: '$3 B USD' },
        { label: t('stats.insurance.price_points'), value: '100+ million' },
        { label: t('stats.insurance.quotes'), value: '1.5+ million' },
        { label: t('stats.insurance.claims'), value: '1+ million' }
      ]
    },
    {
      id: 'ai_agents',
      title: t('stats.categories.ai_agents'),
      stats: [
        { label: t('stats.ai_agents.core'), value: '3' },
        { label: t('stats.ai_agents.specialized'), value: '4' },
        { label: t('stats.ai_agents.data'), value: '4' },
        { label: t('stats.ai_agents.productivity'), value: '3' },
        { label: t('stats.ai_agents.communication'), value: '3' }
      ]
    },
    {
      id: 'data_catalog',
      title: t('stats.categories.data_catalog'),
      stats: [
        { label: t('stats.data_catalog.insurance_quote'), value: '1,500+' },
        { label: t('stats.data_catalog.property'), value: '550+' },
        { label: t('stats.data_catalog.vehicle'), value: '400+' },
        { label: t('stats.data_catalog.telematics'), value: '150+' },
        { label: t('stats.data_catalog.languages'), value: '🇨🇦 🇺🇸 🇬🇧 🇩🇪 🇪🇸 🇲🇽 🇫🇷 🇳🇱 🇹🇷' }
      ]
    },
    {
      id: 'engineering',
      title: t('stats.categories.engineering'),
      stats: [
        { label: t('stats.engineering.frontend'), value: '2,167 Files' },
        { label: t('stats.engineering.lines'), value: '310,788' },
        { label: t('stats.engineering.comments'), value: '15.47%' },
        { label: t('stats.engineering.complexity'), value: '7.93' },
        { label: t('stats.engineering.code_smells'), value: '1,355' },
        // { label: t('stats.engineering.stack'), value: 'React, JavaScript, HTML, CSS' }
      ]
    },
    {
      id: 'architecture',
      title: t('stats.categories.architecture'),
      stats: [
        { label: t('stats.architecture.ai'), value: 'Infrastructure' },
        { label: t('stats.architecture.learning'), value: 'Learning Modules' },
        { label: t('stats.architecture.model'), value: 'Multi Modal Actuarial Distributions' },
        { label: t('stats.architecture.scalability'), value: 'Full-stack operations' },
        { label: t('stats.architecture.optimization'), value: 'Real-time cost minimization' }
      ]
    },
    {
      id: 'iot_data',
      title: t('stats.categories.iot_data'),
      stats: [
        { label: t('stats.iot_data.distance'), value: '50+ M' },
        { label: t('stats.iot_data.distracted'), value: '10,000+ hours' }
      ]
    }
  ], [t]);

  const currentCategory = categories[currentCategoryIndex];
  
  // Memoize map data categories
  const mapCategories = useMemo(() => 
    Object.entries(mapData.categories).map(([key, category]) => ({
      id: key,
      name: category.name
    })), 
  []);

  // Navigation handlers
  const handlePrevious = useCallback(() => {
    setCurrentCategoryIndex((prev) => 
      prev === 0 ? categories.length - 1 : prev - 1
    );
  }, [categories.length]);

  const handleNext = useCallback(() => {
    setCurrentCategoryIndex((prev) => 
      prev === categories.length - 1 ? 0 : prev + 1
    );
  }, [categories.length]);

  // Map category change handler
  const handleMapCategoryChange = useCallback((categoryId) => {
    setActiveMapCategory(categoryId);
    // Force map component to re-render with new key
    setMapKey(prev => prev === categoryId ? `${categoryId}-1` : categoryId);
  }, []);

  // Ensure the map is properly initialized with the default category
  useEffect(() => {
    if (styleLoaded) {
      // Force a re-render of the map component after it's loaded
      setMapKey(`${activeMapCategory}-loaded`);
    }
  }, [styleLoaded, activeMapCategory]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        duration: 0.5,
        staggerChildren: 0.1
      }
    },
    exit: { 
      opacity: 0,
      transition: { duration: 0.3 }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 }
  };

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div className={styles.titleContainer}>
          <h2 className={styles.title}>{t('data_strength_title')}</h2>
          {/* <button 
            className={styles.infoButton}
            title={t('data_strength_info')}
          >
            <FiInfo size={16} />
          </button> */}
        </div>
      </div>

      {/* Map Section */}
      <div className={styles.mapSection}>
        <div className={styles.mapCategoryButtons}>
          {mapCategories.map((category) => (
            <button
              key={category.id}
              className={`${styles.mapCategoryButton} ${activeMapCategory === category.id ? styles.activeMapCategory : ''}`}
              onClick={() => handleMapCategoryChange(category.id)}
            >
              {category.name}
            </button>
          ))}
        </div>
        
        <div className={styles.mapContainer}>
          {!styleLoaded && (
            <div className={styles.mapLoading}>
              <div className={styles.loadingSpinner}></div>
              {t('loading_map')}
            </div>
          )}
          <MapComponent 
            key={mapKey}
            data={mapData.categories[activeMapCategory]}
            category={activeMapCategory}
            onStyleLoad={handleStyleLoad}
            mapData={mapData}
          />
        </div>
      </div>

      {/* Stats Carousel Section */}
      <div className={styles.statsSection}>
        <div className={styles.categoryTabs}>
          {categories.map((category, index) => (
            <button
              key={category.id}
              className={`${styles.categoryTab} ${index === currentCategoryIndex ? styles.activeTab : ''}`}
              onClick={() => setCurrentCategoryIndex(index)}
            >
              {category.title}
              {index === currentCategoryIndex && (
                <motion.div 
                  className={styles.activeIndicator}
                  layoutId="activeIndicator"
                />
              )}
            </button>
          ))}
        </div>

        <div className={styles.statsContainer}>
          <button
            className={`${styles.navButton} ${styles.prevButton}`}
            onClick={handlePrevious}
            aria-label="Previous category"
          >
            <FiChevronLeft className={styles.navIcon} />
          </button>

          <AnimatePresence mode="wait">
            <motion.div 
              key={currentCategory.id}
              className={styles.statsContent}
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <motion.h3 
                className={styles.categoryTitle}
                variants={itemVariants}
              >
                {currentCategory.title}
              </motion.h3>
              
              <motion.div 
                className={styles.statsGrid}
                variants={containerVariants}
              >
                {currentCategory.stats.map((stat, index) => (
                  <motion.div 
                    key={index}
                    className={styles.statItem}
                    variants={itemVariants}
                  >
                    <div className={styles.statValue}>{stat.value}</div>
                    <div className={styles.statLabel}>{stat.label}</div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </AnimatePresence>

          <button
            className={`${styles.navButton} ${styles.nextButton}`}
            onClick={handleNext}
            aria-label="Next category"
          >
            <FiChevronRight className={styles.navIcon} />
          </button>
        </div>

        <div className={styles.categoryCounter}>
          <span className={styles.currentIndex}>{currentCategoryIndex + 1}</span>
          <span className={styles.separator}>/</span>
          <span className={styles.totalCategories}>{categories.length}</span>
        </div>
      </div>
    </div>
  );
};

export default GlobalStatsCard;